import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, websiteNode, serviceNode } from "@/lib/seo"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Input } from "@/components/ui/input"
import { firstTouch } from "@/lib/attribution"
import { Select } from "@/components/ui/select"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Button } from "@/components/ui/button"
import { Field, ErrorSummary, errorId } from "@/components/ui/form-field"
import { track } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

const DESCRIPTION =
  "Apply for a Podcast Growth Fit Call with FullCast — a focused conversation about your offer, audience, and whether a podcast-led relationship strategy fits your business."

const SITUATIONS = [
  { value: "launch", label: "I'm ready to launch a new show" },
  { value: "scale", label: "I have a show and want to scale it" },
  { value: "guidance", label: "I have an idea and need guidance on the concept" },
  { value: "overwhelmed", label: "I've started and feel overwhelmed by the moving parts" },
]

const PAID_CLIENTS = [
  { value: "none", label: "I don't have paid clients yet" },
  { value: "1-5", label: "1–5" },
  { value: "5-20", label: "5–20" },
  { value: "20-50", label: "20–50" },
  { value: "50-plus", label: "50+" },
]

const CLIENT_LTV = [
  { value: "1-5k", label: "$1K–$5K" },
  { value: "5-10k", label: "$5K–$10K" },
  { value: "10-25k", label: "$10K–$25K" },
  { value: "25k-plus", label: "$25K+" },
]

const RELATIONSHIP = [
  { value: "active", label: "Yes, I'll be actively involved" },
  { value: "with_guidance", label: "Open to it with your guidance" },
  { value: "outsource", label: "I'd prefer to fully outsource it" },
]

export default function Apply() {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    website: "",
    podcast_situation: "",
    paid_clients: "",
    client_ltv: "",
    willing_to_invest: "",
    relationship_willingness: "",
    referrer: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    landing_path: "",
    landing_query: "",
    click_ids: "",
    nickname: "", // honeypot — must stay empty; real users never see this field
  })

  // Attach the session's FIRST touch, captured in the client entrypoint on the
  // landing page. Reading document.referrer here instead would credit whatever
  // page preceded /apply, which for an SPA journey is not the same thing.
  useEffect(() => {
    const touch = firstTouch()
    form.setData((data) => ({
      ...data,
      referrer: touch.referrer,
      utm_source: touch.utm_source,
      utm_medium: touch.utm_medium,
      utm_campaign: touch.utm_campaign,
      utm_term: touch.utm_term,
      utm_content: touch.utm_content,
      landing_path: touch.landing_path,
      landing_query: touch.landing_query,
      click_ids: JSON.stringify(touch.click_ids),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    track("Application Submitted")
    form.post("/apply")
  }

  const base = (props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      <SeoHead
        title="Apply for a Fit Call"
        description={DESCRIPTION}
        jsonLd={[organizationNode(base), websiteNode(base), serviceNode(base)]}
      />

      <Section containerClassName="max-w-2xl">
        <Eyebrow>Apply</Eyebrow>
        <h1 className="display-2 mt-4">Apply for a Podcast Growth Fit Call</h1>
        <p className="mt-5 text-lg text-ink-body">
          A few quick questions help us understand your business before we talk,
          so the conversation is focused from the first minute. If it looks like a
          strong fit, you'll be able to book your call right away.
        </p>

        {/* Field keys and control ids match on this form, so the summary needs
            no id mapping. Radio groups resolve to their <fieldset>. */}
        <ErrorSummary errors={errors} subject="application" />

        <form onSubmit={submit} className="mt-10 space-y-8" noValidate>
          {/* Honeypot: hidden from users + assistive tech; bots fill it and get
              silently dropped server-side. */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="nickname">Leave this field empty</label>
            <input
              id="nickname"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.data.nickname}
              onChange={(e) => form.setData("nickname", e.target.value)}
            />
          </div>

          {/* Contact */}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="first_name" error={errors.first_name}>
              <Input
                id="first_name"
                autoComplete="given-name"
                value={form.data.first_name}
                onChange={(e) => form.setData("first_name", e.target.value)}
              />
            </Field>
            <Field label="Last name" htmlFor="last_name" error={errors.last_name}>
              <Input
                id="last_name"
                autoComplete="family-name"
                value={form.data.last_name}
                onChange={(e) => form.setData("last_name", e.target.value)}
              />
            </Field>
          </div>

          <Field label="Email" htmlFor="email" error={errors.email}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
          </Field>

          {/* Optional on purpose. A qualified applicant who leaves a number can
              be called within minutes instead of waiting on a booking, but a
              required phone field on an already-long form costs submissions. */}
          <Field
            label="Phone"
            htmlFor="phone"
            optional
            hint="If you'd rather we just call you, leave a number and we will."
            error={errors.phone}
          >
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              value={form.data.phone}
              onChange={(e) => form.setData("phone", e.target.value)}
            />
          </Field>

          <Field label="Website or LinkedIn" htmlFor="website" optional error={errors.website}>
            <Input
              id="website"
              type="text"
              inputMode="url"
              placeholder="https://"
              value={form.data.website}
              onChange={(e) => form.setData("website", e.target.value)}
            />
          </Field>

          {/* Business */}
          <Field
            label="Where are you with your podcast?"
            htmlFor="podcast_situation"
            error={errors.podcast_situation}
          >
            <Select
              id="podcast_situation"
              value={form.data.podcast_situation}
              onChange={(e) => form.setData("podcast_situation", e.target.value)}
            >
              <option value="" disabled>
                Select one…
              </option>
              {SITUATIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label="How many paid clients do you work with?"
              htmlFor="paid_clients"
              error={errors.paid_clients}
            >
              <Select
                id="paid_clients"
                value={form.data.paid_clients}
                onChange={(e) => form.setData("paid_clients", e.target.value)}
              >
                <option value="" disabled>
                  Select one…
                </option>
                {PAID_CLIENTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Average lifetime value of a client?"
              htmlFor="client_ltv"
              error={errors.client_ltv}
            >
              <Select
                id="client_ltv"
                value={form.data.client_ltv}
                onChange={(e) => form.setData("client_ltv", e.target.value)}
              >
                <option value="" disabled>
                  Select one…
                </option>
                {CLIENT_LTV.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {/* Investment + involvement.
              A radio group is described by its own error message via
              aria-describedby on the fieldset, since there's no single input
              to attach it to. */}
          <fieldset
            id="willing_to_invest"
            tabIndex={-1}
            className="space-y-3 focus:outline-none"
            aria-invalid={errors.willing_to_invest ? true : undefined}
            aria-describedby={
              errors.willing_to_invest ? errorId("willing_to_invest") : undefined
            }
          >
            <legend>
              If it's a strong fit, are you willing and able to invest in a
              6-month partnership?
            </legend>
            {errors.willing_to_invest && (
              <p id={errorId("willing_to_invest")} className="text-xs text-danger-display">
                {errors.willing_to_invest}
              </p>
            )}
            <RadioGroup>
              <label className="flex items-center gap-3 font-normal text-ink-body">
                <Radio
                  name="willing_to_invest"
                  value="true"
                  checked={form.data.willing_to_invest === "true"}
                  onChange={(e) => form.setData("willing_to_invest", e.target.value)}
                />
                Yes, if you can show me a solid plan
              </label>
              <label className="flex items-center gap-3 font-normal text-ink-body">
                <Radio
                  name="willing_to_invest"
                  value="false"
                  checked={form.data.willing_to_invest === "false"}
                  onChange={(e) => form.setData("willing_to_invest", e.target.value)}
                />
                Not at this point
              </label>
            </RadioGroup>
          </fieldset>

          <fieldset
            id="relationship_willingness"
            tabIndex={-1}
            className="space-y-3 focus:outline-none"
            aria-invalid={errors.relationship_willingness ? true : undefined}
            aria-describedby={
              errors.relationship_willingness
                ? errorId("relationship_willingness")
                : undefined
            }
          >
            <legend>
              Will you personally participate in guest outreach and
              relationship-building?
            </legend>
            {errors.relationship_willingness && (
              <p
                id={errorId("relationship_willingness")}
                className="text-xs text-danger-display"
              >
                {errors.relationship_willingness}
              </p>
            )}
            <RadioGroup>
              {RELATIONSHIP.map((o) => (
                <label
                  key={o.value}
                  className="flex items-center gap-3 font-normal text-ink-body"
                >
                  <Radio
                    name="relationship_willingness"
                    value={o.value}
                    checked={form.data.relationship_willingness === o.value}
                    onChange={(e) => form.setData("relationship_willingness", e.target.value)}
                  />
                  {o.label}
                </label>
              ))}
            </RadioGroup>
          </fieldset>

          <div className="pt-2">
            <Button type="submit" size="lg" disabled={form.processing}>
              {form.processing ? "Submitting…" : "Submit application"}
            </Button>
            <p className="mt-3 text-sm text-ink-muted">
              No public pricing — we'll discuss specifics together if it's a fit.
            </p>
          </div>
        </form>
      </Section>
    </MarketingLayout>
  )
}
